import { useCallback, useRef, useState } from "react";
import { ItemArt } from "../art/ItemArt";
import { Coins } from "../components/Coins";
import { Modal } from "../components/Modal";
import { itemsOfKind, MAX_LEVEL, type Item, type ItemKind } from "../content/items";
import { balanceOf, levelHeld, nextLevel, type OwnedItems } from "../game/items";
import type { SaveState } from "../save/schema";
import { speak, stopSpeaking } from "../speech/speech";
import "./Shop.css";

interface Props {
  save: SaveState;
  /** Buy the next level of an item. False when the coins don't cover it (a race with another device). */
  onBuy: (item: Item) => boolean;
  /** Go and fit items to a ninja (My ninjas). */
  onFit: () => void;
  onBack: () => void;
}

const SHELF: Record<ItemKind, { title: string; blurb: string }> = {
  active: { title: "Use in a fight", blurb: "A button in battle. Press it once a fight." },
  passive: { title: "Wear", blurb: "Always on while a ninja wears it." },
};

/** "Buy · 80", "Upgrade · 120" or "Maxed". */
export function buyLabel(items: OwnedItems, item: Item): string {
  const next = nextLevel(items, item);
  if (!next) return "Maxed";
  return `${next.level === 1 ? "Buy" : "Upgrade"} · ${next.at.price}`;
}

/** What the card says the item does: the level on offer, or the top level once maxed. */
export function offerText(items: OwnedItems, item: Item): string {
  const next = nextLevel(items, item);
  return (next ? next.at : item.levels[item.levels.length - 1]!).text;
}

/** The Dojo Shop: nine items on two shelves, bought with coins earned by playing. */
export function Shop({ save, onBuy, onFit, onBack }: Props) {
  const balance = balanceOf(save);
  const [confirming, setConfirming] = useState<Item | null>(null);
  const [bought, setBought] = useState<{ item: Item; level: number } | null>(null);
  const buyBtn = useRef<HTMLButtonElement>(null);
  const fitBtn = useRef<HTMLButtonElement>(null);
  const audio = save.settings.audio;

  const closeConfirm = useCallback(() => setConfirming(null), []);
  const closeBought = useCallback(() => setBought(null), []);
  const confirm = useCallback(() => {
    if (!confirming) return;
    const next = nextLevel(save.items, confirming);
    setConfirming(null);
    if (next && onBuy(confirming)) setBought({ item: confirming, level: next.level });
  }, [confirming, save.items, onBuy]);

  const hear = (item: Item) => {
    stopSpeaking();
    speak(`${item.name}. ${offerText(save.items, item)}.`);
  };

  const shelf = (kind: ItemKind) => (
    <section className="shelf" aria-labelledby={`shelf-${kind}`}>
      <h3 id={`shelf-${kind}`} className="display shelf-title">
        {SHELF[kind].title}
      </h3>
      <p className="shelf-blurb">{SHELF[kind].blurb}</p>
      <ul className="shop-grid">
        {itemsOfKind(kind).map((item) => {
          const held = levelHeld(save.items, item.id);
          const next = nextLevel(save.items, item);
          const price = next?.at.price ?? 0;
          const short = Math.max(0, price - balance);
          const canBuy = !!next && short === 0;
          const Body = audio ? "button" : "div";
          return (
            <li key={item.id} className={"item-card" + (held ? " owned" : "")}>
              <Body
                {...(audio ? { type: "button", onClick: () => hear(item) } : {})}
                className="item-body"
                aria-label={
                  audio ? `${item.name}. ${offerText(save.items, item)}. Read aloud` : undefined
                }
              >
                <ItemArt id={item.id} className="item-icon" />
                <span className="item-name display">{item.name}</span>
                <span className="pips" aria-label={`Level ${held} of ${item.levels.length}`}>
                  {Array.from({ length: item.levels.length }, (_, i) => (
                    <i key={i} className={i < held ? "on" : ""} />
                  ))}
                </span>
                <span className="item-text">{offerText(save.items, item)}</span>
              </Body>
              <button
                type="button"
                className={"mini buy" + (canBuy ? " can" : "")}
                disabled={!canBuy}
                aria-label={
                  next
                    ? `${next.level === 1 ? "Buy" : "Upgrade"} ${item.name} for ${price} coins` +
                      (short ? `. Earn ${short} more` : "")
                    : `${item.name} is maxed`
                }
                onClick={() => setConfirming(item)}
              >
                {next ? (
                  <>
                    {next.level === 1 ? "Buy" : "Upgrade"} <Coins count={price} />
                  </>
                ) : (
                  <>Maxed ✓</>
                )}
              </button>
              {next && short > 0 && <span className="earn-more">Earn {short} more</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );

  const offer = confirming ? nextLevel(save.items, confirming) : null;
  return (
    <section className="screen shop">
      <header className="shop-head">
        <h2 className="display shop-title">Dojo Shop</h2>
        <Coins count={balance} className="shop-coins" />
      </header>
      <p className="shop-sub">
        Coins come from battles, practice and trials. Buy an item, then fit it to a ninja in My
        ninjas.
      </p>
      {shelf("active")}
      {shelf("passive")}
      <div className="row shop-row">
        <button type="button" className="ghost" onClick={onFit}>
          My ninjas
        </button>
        <button type="button" className="big display shop-back" onClick={onBack}>
          Back
        </button>
      </div>

      <Modal
        open={!!confirming && !!offer}
        label="Buy?"
        onClose={closeConfirm}
        initialFocus={buyBtn}
      >
        {confirming && offer && (
          <div className="panel welcome buy-box">
            <ItemArt id={confirming.id} className="buy-art" />
            <h2 className="display">
              {offer.level === 1 ? "Buy" : "Upgrade"} {confirming.name}
              {offer.level > 1 ? ` to level ${offer.level}` : ""} for {offer.at.price} coins?
            </h2>
            <p>
              {offer.at.text}. You&apos;ll have {balance - offer.at.price} left.
            </p>
            <button ref={buyBtn} type="button" className="big display" onClick={confirm}>
              {offer.level === 1 ? "Buy it" : "Upgrade it"}
            </button>
            <button type="button" className="ghost" onClick={closeConfirm}>
              Not now
            </button>
          </div>
        )}
      </Modal>
      <Modal open={!!bought} label="Bought" onClose={closeBought} initialFocus={fitBtn}>
        {bought && (
          <div className="panel welcome buy-box">
            <ItemArt id={bought.item.id} className="buy-art reveal" />
            <h2 className="display">
              {bought.item.name}
              {bought.level > 1 ? ` level ${bought.level}` : ""}!
            </h2>
            <p>
              {bought.item.levels[bought.level - 1]!.text}.
              {bought.level === MAX_LEVEL || bought.level === bought.item.levels.length
                ? " That's as good as it gets!"
                : ""}
            </p>
            <button ref={fitBtn} type="button" className="big display" onClick={onFit}>
              {bought.level === 1 ? "Fit it to a ninja" : "See my ninjas"}
            </button>
            <button type="button" className="ghost" onClick={closeBought}>
              Keep shopping
            </button>
          </div>
        )}
      </Modal>
    </section>
  );
}
